import { useCallback, useEffect, useState } from 'react';
import './AdminPage.css';

const KEY_STORAGE = 'kya_admin_key';
const ADMIN_BASE = '/api/admin';

async function adminFetch(action, key) {
  const res = await fetch(`${ADMIN_BASE}/${action}`, {
    headers: { 'x-admin-key': key },
  });
  if (res.status === 401) {
    const err = new Error('unauthorized');
    err.code = 'unauthorized';
    throw err;
  }
  if (res.status === 503) {
    const err = new Error('not_configured');
    err.code = 'not_configured';
    throw err;
  }
  if (!res.ok) {
    throw new Error(`request failed (${res.status})`);
  }
  return res.json();
}

function formatWhen(iso) {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

function PromoCodesPanel({ adminKey }) {
  const [data, setData] = useState(null);
  const [status, setStatus] = useState('loading'); // loading | ready | error
  const [error, setError] = useState('');
  const [copied, setCopied] = useState('');

  const load = useCallback(async () => {
    setStatus('loading');
    setError('');
    try {
      const result = await adminFetch('promo-codes', adminKey);
      setData(result);
      setStatus('ready');
    } catch (err) {
      setError(err.message);
      setStatus('error');
    }
  }, [adminKey]);

  useEffect(() => {
    load();
  }, [load]);

  const copy = useCallback(async (text, label) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(label);
      setTimeout(() => setCopied(''), 1800);
    } catch (e) {
      /* clipboard blocked */
    }
  }, []);

  const claims = data?.claims ?? [];
  const pct = data ? Math.round((data.claimed / data.total) * 100) : 0;

  return (
    <section className="admin-panel">
      <div className="admin-panel-head">
        <h2>iOS Promo Codes</h2>
        <button type="button" className="admin-ghost-btn" onClick={load} disabled={status === 'loading'}>
          {status === 'loading' ? 'Refreshing…' : 'Refresh'}
        </button>
      </div>

      {status === 'error' && <p className="admin-error">Couldn't load promo data: {error}</p>}

      {data && (
        <>
          <div className="admin-stats">
            <div className="admin-stat">
              <span className="admin-stat-num">{data.claimed}</span>
              <span className="admin-stat-label">Claimed</span>
            </div>
            <div className="admin-stat">
              <span className="admin-stat-num">{data.remaining}</span>
              <span className="admin-stat-label">Remaining</span>
            </div>
            <div className="admin-stat">
              <span className="admin-stat-num">{data.total}</span>
              <span className="admin-stat-label">Total</span>
            </div>
          </div>

          <div className="admin-bar">
            <div className="admin-bar-fill" style={{ width: `${pct}%` }} />
          </div>

          {claims.length > 0 ? (
            <>
              <div className="admin-table-actions">
                <button
                  type="button"
                  className="admin-ghost-btn"
                  onClick={() =>
                    copy(claims.map((c) => c.code).join('\n'), 'all')
                  }
                >
                  {copied === 'all' ? 'Copied' : 'Copy all used codes'}
                </button>
              </div>
              <div className="admin-table-wrap">
                <table className="admin-table">
                  <thead>
                    <tr>
                      <th>#</th>
                      <th>Code</th>
                      <th>Claimed</th>
                      <th aria-label="Copy" />
                    </tr>
                  </thead>
                  <tbody>
                    {claims.map((c, i) => (
                      <tr key={c.code}>
                        <td>{i + 1}</td>
                        <td className="admin-code">{c.code}</td>
                        <td>{formatWhen(c.at)}</td>
                        <td>
                          <button
                            type="button"
                            className="admin-copy-cell"
                            onClick={() => copy(c.code, c.code)}
                            aria-label={`Copy ${c.code}`}
                          >
                            {copied === c.code ? '✓' : 'Copy'}
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          ) : (
            <p className="admin-muted">No codes claimed yet.</p>
          )}
        </>
      )}
    </section>
  );
}

export default function AdminPage() {
  const [adminKey, setAdminKey] = useState('');
  const [authed, setAuthed] = useState(false);
  const [checking, setChecking] = useState(true);
  const [input, setInput] = useState('');
  const [loginError, setLoginError] = useState('');

  // Try a stored key on first load.
  useEffect(() => {
    let stored = '';
    try {
      stored = window.sessionStorage.getItem(KEY_STORAGE) || '';
    } catch (e) {
      /* sessionStorage unavailable */
    }
    if (!stored) {
      setChecking(false);
      return;
    }
    adminFetch('promo-codes', stored)
      .then(() => {
        setAdminKey(stored);
        setAuthed(true);
      })
      .catch(() => {
        try {
          window.sessionStorage.removeItem(KEY_STORAGE);
        } catch (e) {
          /* ignore */
        }
      })
      .finally(() => setChecking(false));
  }, []);

  const submit = useCallback(
    async (e) => {
      e.preventDefault();
      setLoginError('');
      const candidate = input.trim();
      if (!candidate) return;
      try {
        await adminFetch('promo-codes', candidate);
        try {
          window.sessionStorage.setItem(KEY_STORAGE, candidate);
        } catch (err) {
          /* ignore */
        }
        setAdminKey(candidate);
        setAuthed(true);
      } catch (err) {
        if (err.code === 'not_configured') {
          setLoginError('Admin is not configured on the server (ADMIN_KEY is unset).');
        } else if (err.code === 'unauthorized') {
          setLoginError('That key was not accepted.');
        } else {
          setLoginError(err.message);
        }
      }
    },
    [input]
  );

  const logout = useCallback(() => {
    try {
      window.sessionStorage.removeItem(KEY_STORAGE);
    } catch (e) {
      /* ignore */
    }
    setAdminKey('');
    setAuthed(false);
    setInput('');
  }, []);

  if (checking) {
    return (
      <div className="admin-page">
        <div className="admin-shell">
          <p className="admin-muted">Checking…</p>
        </div>
      </div>
    );
  }

  if (!authed) {
    return (
      <div className="admin-page">
        <form className="admin-shell admin-login" onSubmit={submit}>
          <h1 className="admin-title">Admin</h1>
          <p className="admin-muted">Enter the admin key to continue.</p>
          <input
            type="password"
            className="admin-input"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            placeholder="Admin key"
            autoComplete="off"
            autoFocus
          />
          {loginError && <p className="admin-error">{loginError}</p>}
          <button type="submit" className="admin-button">
            Unlock
          </button>
        </form>
      </div>
    );
  }

  return (
    <div className="admin-page">
      <div className="admin-shell">
        <header className="admin-header">
          <h1 className="admin-title">Admin</h1>
          <button type="button" className="admin-ghost-btn" onClick={logout}>
            Log out
          </button>
        </header>

        <PromoCodesPanel adminKey={adminKey} />
      </div>
    </div>
  );
}
